import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck, CircleDot } from "lucide-react";
import { api } from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";

const KIND_COLOR = {
  info: "bg-zinc-100 text-zinc-700",
  success: "bg-emerald-100 text-emerald-700",
  warn: "bg-amber-100 text-amber-700",
  error: "bg-red-100 text-red-700",
};

export default function NotificationBell() {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const intervalRef = useRef(null);

  const refreshCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setCount(data.count || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    refreshCount();
    intervalRef.current = setInterval(refreshCount, 20000);
    return () => clearInterval(intervalRef.current);
  }, [refreshCount]);

  const loadAll = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch { /* ignore */ }
  }, []);

  const onOpenChange = (v) => {
    setOpen(v);
    if (v) loadAll();
  };

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    setItems((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
    refreshCount();
  };

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setCount(0);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-lg hover:bg-zinc-100 text-zinc-500 hover:text-zinc-900 transition" data-testid="notif-bell">
          <Bell className="w-4 h-4" />
          {count > 0 && (
            <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-[hsl(var(--brand-highlight))] text-white text-caption font-medium grid place-items-center">
              {count > 9 ? "9+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[380px] p-0 rounded-2xl" data-testid="notif-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100">
          <div className="text-sm font-medium">Notifications</div>
          {count > 0 && (
            <Button variant="ghost" size="sm" onClick={markAll} className="text-xs h-7" data-testid="notif-mark-all">
              <CheckCheck className="w-3 h-3 mr-1" /> Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-zinc-500">No notifications yet.</div>
          ) : (
            items.map((n) => (
              <NotifItem key={n.id} n={n} onMarkRead={markRead} onClose={() => setOpen(false)} />
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function NotifItem({ n, onMarkRead, onClose }) {
  const body = (
    <div className="flex items-start gap-3 px-4 py-3 hover:bg-zinc-50 transition cursor-pointer" onClick={() => { if (!n.read) onMarkRead(n.id); onClose(); }}>
      <span className={`w-7 h-7 rounded-full grid place-items-center shrink-0 ${KIND_COLOR[n.kind] || KIND_COLOR.info}`}>
        <CircleDot className="w-3.5 h-3.5" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{n.title}</div>
        {n.body && <div className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{n.body}</div>}
        <div className="text-caption text-zinc-400 mt-1 font-mono">{new Date(n.created_at).toLocaleString()}</div>
      </div>
      {!n.read && <span className="w-2 h-2 rounded-full bg-[hsl(var(--brand-highlight))] mt-2 shrink-0" />}
    </div>
  );
  return n.link ? <Link to={n.link} className="block border-b border-zinc-100 last:border-0">{body}</Link> : <div className="border-b border-zinc-100 last:border-0">{body}</div>;
}
